import { preparePhoto } from 'rentalui-photo-upload';
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
export const VERTICAL = 'jewelry';

export const client = axios.create({ baseURL: API_BASE_URL });



// Demo images ship as local static assets in this app's own public/images/dummy (same relative
// paths rentalapi's seed data uses) rather than being fetched cross-origin from rentalapi - that
// avoided rentalapi's Cross-Origin-Resource-Policy header blocking <img> loads, and matches how
// TestApplication's microfrontends served images. process.env.PUBLIC_URL is always '' (no
// "homepage" field - this app is served from the root of its own subdomain in both local dev and
// production), so this just resolves to a root-relative path. Swap this for real cloud image
// URLs later.
//
// Owner-uploaded photos (see uploadImages() below) are the one exception - those are real files
// that only exist on rentalapi (routes/uploads.js), not shipped as static assets here, so they
// resolve against API_BASE_URL instead. rentalapi opts /uploads specifically out of its default
// same-origin Cross-Origin-Resource-Policy so these <img> loads aren't blocked (see app.js).
export function imageUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads/')) return `${API_BASE_URL}${path}`;
  return encodeURI(`${process.env.PUBLIC_URL}${path}`);
}

// UPLOAD_Images_to_GCS.md ("faster images") - an uploaded photo is stored as a WebP ladder
// `<base>_320.webp` .. `_2560.webp` and the canonical `_1024` URL is what lands in images[].
// Given any stored image URL, return a `srcSet` spanning those widths so the browser fetches
// only the size it needs; '' for a legacy image (plain .jpg/.png, or a bundled asset) so the
// caller just renders a plain <img src>. Keep VARIANT_WIDTHS in sync with routes/uploads.js.
const VARIANT_WIDTHS = [320, 640, 1024, 1600, 2560];
export function imageSrcSet(url) {
  if (!url) return '';
  const m = /^(.*)_\d+\.webp$/.exec(url);
  if (!m) return '';
  return VARIANT_WIDTHS.map((w) => `${imageUrl(`${m[1]}_${w}.webp`)} ${w}w`).join(', ');
}

// Owner uploads listing photos (multipart/form-data) and gets back URLs to put straight into an
// item's `images` array - see pages/ItemForm.js. Don't set a Content-Type header manually here;
// letting axios/the browser generate it (including the multipart boundary) from the FormData
// instance is required for the server to parse it.
export async function uploadImages(files) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('images', await preparePhoto(file));
  }
  const res = await client.post('/api/uploads/images', formData);
  return res.data.data;
}

// ---- Public browsing ----

export async function listItems(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/items`, { params });
  return res.data;
}

export async function getItem(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}`);
  return res.data.data;
}

// Owner Bookings & Cancellation v2 doc, Story 11 - this item's owner's resolved cancellation
// terms (their negotiated override if they have one, else the platform default) - shown before
// booking, not a generic T&C paragraph. No auth required.
export async function getCancellationTerms(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}/cancellation-terms`);
  return res.data.data;
}

// Fire-and-forget interaction tracking that feeds the "recommended" feed ranking (view/contact/
// wishlist - see rentalapi/routes/items.js). Never throws: callers use this purely as a side
// effect and must not have the UI action itself fail because analytics didn't record.
export async function recordItemEvent(id, type) {
  try {
    await client.post(`/api/${VERTICAL}/items/${id}/events`, { type });
  } catch {
    // best-effort only
  }
}

export async function createBooking(payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings`, payload);
  return res.data;
}

// common/v7 booking_availability - the blocked-spans feed behind <AvailabilityCalendar>. No auth
// required (same as the item page itself).
export async function getAvailability({ resourceId, from, to, resourceType = 'item' } = {}) {
  const res = await client.get(`/api/${VERTICAL}/availability`, { params: { resourceType, resourceId, from, to } });
  return res.data.data;
}

// Owner/admin: replace the listing's blocked date ranges (maintenance / renovation / owner use).
export async function saveBlockedDates(itemId, blockedDates) {
  const res = await client.put(`/api/${VERTICAL}/items/${itemId}/blocked-dates`, { blockedDates });
  return res.data.data;
}

// StocksManagement doc §21/§23 - owner/admin read-only view of a listing's inventory: capacity
// per resource + the InventoryTransaction audit trail.
export async function getInventory(itemId) {
  const res = await client.get(`/api/${VERTICAL}/items/${itemId}/inventory`);
  return res.data.data;
}

// delivery_distance_pricing.md - live price breakdown (rental + platform fee + distance-based
// delivery charge + deposit) for a would-be booking, WITHOUT creating anything. Drives the
// booking-form price panel and the confirm popup. data.deliveryChargeAvailable is false (not an
// error) until the customer picks a delivery address from the map / "detect my location".
export async function getBookingQuote(payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/quote`, payload);
  return res.data.data;
}

// ---- fix_issue3.md ----

// #7 - "You may also like": other live items in the same vertical, same category first.
export async function getRelatedItems(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}/related`);
  return res.data.data;
}

// #3 - APPROVED ratings shown on a listing (public).
export async function getItemRatings(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}/ratings`);
  return res.data.data;
}

// #3 - a logged-in customer with a completed rental of this item rates it. The rating stays
// pending until a website admin approves it.
export async function submitRating(id, { rating, review } = {}) {
  const res = await client.post(`/api/${VERTICAL}/items/${id}/ratings`, { rating, review });
  return res.data;
}

export async function getBooking(id) {
  const res = await client.get(`/api/${VERTICAL}/bookings/${id}`);
  return res.data.data;
}

// ---- Customer self-service on a booking (MyBookings user story 3.2) ----

// Owner Bookings & Cancellation v2 doc, Story 2 - what would be refunded if cancelled right now,
// without actually cancelling. Same requireAuth-gated route a customer or the listing's owner can
// call (see CancelBookingModal).
export async function getCancellationPreview(id) {
  const res = await client.get(`/api/${VERTICAL}/bookings/${id}/cancellation-preview`);
  return res.data.data;
}

// Story 1: reason is required. forceMajeureClaimed/forceMajeureEvidenceUrl are optional (Story
// 10) - only meaningful for a customer's own late cancellation.
export async function cancelBooking(id, { reason, forceMajeureClaimed, forceMajeureEvidenceUrl } = {}) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/cancel`, { reason, forceMajeureClaimed, forceMajeureEvidenceUrl });
  return res.data;
}

// Owner Bookings & Cancellation v2 doc, Story 12 - refund/settlement status + Finance's reviewer
// comments for a cancelled booking, visible to the booking's own customer or its listing's owner
// (not just Finance). 404s if the booking was never cancelled (or had no rentalAmount) - callers
// should treat that as "nothing to show" rather than an error.
export async function getBookingSettlement(id) {
  const res = await client.get(`/api/${VERTICAL}/bookings/${id}/settlement`);
  return res.data.data;
}

export async function requestExtension(id, payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/${id}/extend`, payload);
  return res.data;
}

export async function reportIssue(id, payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/${id}/issue`, payload);
  return res.data;
}

// ---- Owner ----

export async function getMyItems() {
  const res = await client.get(`/api/${VERTICAL}/items/mine`);
  return res.data.data;
}

export async function createItem(payload) {
  const res = await client.post(`/api/${VERTICAL}/items`, payload);
  return res.data.data;
}

export async function updateItem(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/items/${id}`, payload);
  return res.data.data;
}

export async function deleteItem(id) {
  const res = await client.delete(`/api/${VERTICAL}/items/${id}`);
  return res.data;
}

// ---- Bookings ----

export async function getMyBookings() {
  const res = await client.get(`/api/${VERTICAL}/bookings/mine`);
  return res.data.data;
}

export async function updateBookingStatus(id, status) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}`, { status });
  return res.data.data;
}

export async function sendReminder(id) {
  const res = await client.post(`/api/${VERTICAL}/bookings/${id}/remind`);
  return res.data;
}

// ---- Delivery assignment & tracking (Owner Bookings & Cancellation v2 doc, Story 14) ----

// area (Story 16) - the booking's delivery area/city, so whoever covers it sorts first and gets
// flagged `matchesArea` in the response - see AssignDeliveryModal.js. date (Story 17) - the
// booking's scheduled/start date, so anyone with an overlapping RentalUser.unavailability window
// is flagged `available: false` instead of being offered as if nothing were wrong.
export async function getDeliveryStaff(area, date) {
  const res = await client.get(`/api/${VERTICAL}/bookings/delivery-staff`, { params: { area, date } });
  return res.data.data;
}

export async function assignDelivery(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/assign-delivery`, payload);
  return res.data.data;
}

// Role-scoped server-side (see routes/ownerCrud.js's GET .../bookings/deliveries) - the same call
// returns a different, narrower slice of bookings depending on who's logged in. Paginated
// (20/page default) for infinite-scroll lists; `when` is 'upcoming' | 'past' | 'all'.
export async function getDeliveries({ range, search, page } = {}) {
  const res = await client.get(`/api/${VERTICAL}/bookings/deliveries`, { params: { range, search, page } });
  return res.data;
}

// ---- SLA auto-assignment & unavailability (Owner Bookings & Cancellation v2 doc, Story 17) ----

// Delivery person declines an assignment (unavailable on the date/time, etc.) - frees the slot and
// returns { data: updatedBooking, suggestedReplacement } so the owner/admin can reassign in one
// click instead of starting from scratch.
export async function declineDelivery(id, reason) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/decline-delivery`, { reason });
  return res.data;
}

export async function getMyUnavailability() {
  const res = await client.get(`/api/${VERTICAL}/bookings/my-unavailability`);
  return res.data.data;
}

export async function addUnavailability(payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/my-unavailability`, payload);
  return res.data.data;
}

export async function removeUnavailability(entryId) {
  const res = await client.delete(`/api/${VERTICAL}/bookings/my-unavailability/${entryId}`);
  return res.data.data;
}

// ---- Rental owner's pickup address (delivery ticket pickup/drop/distance doc) ----
// The owner's own pickup/business address, stored on RentalUser.address. Used as the OUTBOUND
// delivery ticket's pickup address (and the RETURN ticket's drop). Set from PickupAddressCard
// on the Owner dashboard.
export async function getMyPickupAddress() {
  const res = await client.get(`/api/${VERTICAL}/bookings/my-pickup-address`);
  return res.data.data;
}

export async function saveMyPickupAddress(address) {
  const res = await client.put(`/api/${VERTICAL}/bookings/my-pickup-address`, address);
  return res.data.data;
}

// ---- Google Maps address search (server-side proxy - no key in the browser) ----
// Used by AddressAutocomplete on every address form. All three degrade to empty/null when
// rentalapi has no GOOGLE_MAPS_API_KEY, so the caller just falls back to plain typed fields.
export async function addressAutocomplete(q, sessionToken) {
  const res = await client.get('/api/maps/autocomplete', { params: { q, sessionToken } });
  return res.data.data?.predictions || [];
}

export async function addressPlaceDetails(placeId, sessionToken) {
  const res = await client.get(`/api/maps/place/${encodeURIComponent(placeId)}`, { params: { sessionToken } });
  return res.data.data; // normalized address or null
}

export async function addressReverseGeocode(lat, lng) {
  const res = await client.get('/api/maps/reverse-geocode', { params: { lat, lng } });
  return res.data.data; // normalized address or null
}

// ---- Owner fulfillment actions (MyBookings user story 5.2) ----

export async function resolveExtension(id, status) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/extend`, { status });
  return res.data.data;
}

export async function updateDeliveryStatus(id, deliveryStatus) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/delivery`, { deliveryStatus });
  return res.data.data;
}

export async function updateReturnStatus(id, returnStatus, kmDriven, opts = {}) {
  const body = { returnStatus };
  if (kmDriven != null && kmDriven !== '') body.kmDriven = Number(kmDriven);
  // A website admin can force a COMPLETED transition past the "payment not received" gate.
  if (opts.override) { body.override = true; body.overrideReason = opts.overrideReason || ''; }
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/return`, body);
  return res.data.data;
}

export async function reportDamage(id, payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/${id}/damage`, payload);
  return res.data.data;
}

// ---- Admin-only financial finalization (MyBookings user story 10.1) ----

export async function updateDepositStatus(id, depositStatus) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/deposit`, { depositStatus });
  return res.data.data;
}

// Owner Bookings & Cancellation v2 doc, Story 15 - Owner (own listing) or Admin confirms a
// REFUND_PENDING deposit was actually sent (bank/UPI transfer or handed back in cash). The only
// way depositStatus becomes REFUNDED, and therefore the only way the order can count as
// "Completed" - see OwnerDashboard.js/MyBookings.js's bucketing.
export async function markRefundSent(id, { method, notes } = {}) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/deposit/refund-sent`, { method, notes });
  return res.data.data;
}

// payements_requirements.md - the rental owner (own listings) records a payment they received
// when the online gateway is down. Owner entries are limited server-side to money that reached
// the owner (OWNER_CASH / OWNER_DIRECT_ONLINE) and flagged for finance to reconcile. Admin /
// finance can also call this for any collection source.
export async function recordBookingPayment(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/payment`, payload);
  return res.data.data;
}

// Finance / admin confirms a rental owner's manual payment entry.
export async function reconcileBookingPayment(id, note) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/payment/reconcile`, { note });
  return res.data.data;
}

export async function updatePayoutStatus(id, payoutStatus) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/payout`, { payoutStatus });
  return res.data.data;
}

// ---- Delivery / Operations (MyBookings user story 9.2) ----

export async function getTodaysDeliveries() {
  const res = await client.get(`/api/${VERTICAL}/delivery/today`);
  return res.data.data;
}

export async function markPickedUp(id) {
  const res = await client.patch(`/api/${VERTICAL}/delivery/${id}/pickup`);
  return res.data.data;
}

export async function markDelivered(id) {
  const res = await client.patch(`/api/${VERTICAL}/delivery/${id}/delivered`);
  return res.data.data;
}

// ---- Admin ----

// admin_listings_visibility.md - status is 'active' (default view, hides disabled), 'disabled',
// or 'all'. Rows carry ownerName/ownerEmail/ownerPhone + createdAt, sorted active-and-recent first.
// Returns the full { data, pagination } envelope (server-side paginated). Pass { status, page, limit }.
export async function adminListItems(params = {}) {
  const opts = typeof params === 'string' ? { status: params } : params;
  const res = await client.get(`/api/admin/${VERTICAL}/items`, { params: opts });
  return res.data;
}

export async function adminDeleteItem(id) {
  const res = await client.delete(`/api/admin/${VERTICAL}/items/${id}`);
  return res.data;
}

// fix_issue3.md #3 - the ratings moderation queue and the approve/reject action.
export async function adminListRatings(status) {
  const res = await client.get(`/api/admin/${VERTICAL}/ratings`, { params: status ? { status } : {} });
  return res.data.data;
}

export async function adminDecideRating(id, decision) {
  const res = await client.patch(`/api/admin/${VERTICAL}/ratings/${id}`, { decision });
  return res.data;
}

// fix_issue3.md #3 - worst ratings (<= 2). An owner sees their own listings' worst ratings; the
// website owner (admin) sees every listing's.
export async function getLowRatings() {
  const res = await client.get(`/api/${VERTICAL}/items/low-ratings`);
  return res.data.data;
}

// Returns the full { data, pagination } envelope (server-side paginated).
export async function adminListBookings(filters = {}) {
  const res = await client.get(`/api/admin/${VERTICAL}/bookings`, { params: filters });
  return res.data;
}

// user_management_and_delivery_kyc.md - paginated + filterable (status active/disabled/all, role,
// free-text q). Returns { data, pagination, pendingDeliveryRequests }.
export async function adminListUsers(params = {}) {
  const res = await client.get(`/api/admin/${VERTICAL}/users`, { params });
  return res.data;
}

export async function adminGetUser(id) {
  const res = await client.get(`/api/admin/${VERTICAL}/users/${id}`);
  return res.data.data;
}

export async function adminSetUserStatus(id, status) {
  const res = await client.patch(`/api/admin/${VERTICAL}/users/${id}/status`, { status });
  return res.data.data;
}

export async function adminSetUserRoles(id, roles) {
  const res = await client.patch(`/api/admin/${VERTICAL}/users/${id}/roles`, { roles });
  return res.data.data;
}

// Admin-created accounts for roles that aren't self-serve via signup. For role 'delivery' the
// payload must include kyc:{aadhaarNumber,aadhaarCardUrl} and address:{line1,city,pincode}.
export async function adminCreateUser(payload) {
  const res = await client.post(`/api/admin/${VERTICAL}/users`, payload);
  return res.data.data;
}

// Re-issue a one-time temporary password for an onboarded/staff account. Returns { tempPassword }.
export async function adminResetTempPassword(userId) {
  const res = await client.post(`/api/admin/${VERTICAL}/users/${userId}/reset-temp-password`);
  return res.data;
}

export async function adminUpdateServiceArea(userId, serviceArea) {
  const res = await client.patch(`/api/admin/${VERTICAL}/users/${userId}/service-area`, { serviceArea });
  return res.data.data;
}

// The website owner invites a privately-onboarded delivery person to also serve other businesses
// on the platform (common/v3/delivery_agent_pool_scoping.md). The delivery person accepts/declines
// from their own dashboard.
export async function adminInviteToSharedPool(userId) {
  const res = await client.post(`/api/admin/${VERTICAL}/users/${userId}/sharing-invite`);
  return res.data.data;
}
export async function getMySharingInvite() {
  const res = await client.get(`/api/${VERTICAL}/delivery/sharing-invite`);
  return res.data.data; // { sharingSupported, availableForOtherOwners, onboardedByOwner, sharingInvite }
}
export async function respondToSharingInvite(accept) {
  const res = await client.patch(`/api/${VERTICAL}/delivery/sharing-invite`, { accept });
  return res.data.data;
}

// ---- Delivery-person requests (owner -> website owner) ----
export async function requestDeliveryPerson(payload) {
  const res = await client.post(`/api/${VERTICAL}/bookings/delivery-person-requests`, payload);
  return res.data.data;
}
export async function listMyDeliveryPersonRequests() {
  const res = await client.get(`/api/${VERTICAL}/bookings/delivery-person-requests`);
  return res.data.data;
}
export async function adminListDeliveryPersonRequests(status) {
  const res = await client.get(`/api/admin/${VERTICAL}/delivery-person-requests`, { params: status ? { status } : {} });
  return res.data.data;
}
export async function adminDecideDeliveryPersonRequest(id, decision, reviewerNote, availableForOtherOwners) {
  const body = { decision, reviewerNote };
  if (availableForOtherOwners !== undefined) body.availableForOtherOwners = availableForOtherOwners;
  const res = await client.patch(`/api/admin/${VERTICAL}/delivery-person-requests/${id}`, body);
  return res.data; // { data, createdUser?, tempPassword? }
}

// ---- Delivery-person payables (delivery_person_payables.md) ----
// The delivery person's own receivables from business owners.
export async function getMyDeliveryPayables(status) {
  const res = await client.get(`/api/${VERTICAL}/delivery/payables`, { params: status ? { status } : {} });
  return res.data; // { data, summaryByOwner }
}
export async function remindOwnerPayable(id) {
  const res = await client.post(`/api/${VERTICAL}/delivery/payables/${id}/remind`);
  return res.data.data;
}
export async function escalatePayable(id, note) {
  const res = await client.post(`/api/${VERTICAL}/delivery/payables/${id}/escalate`, { note });
  return res.data.data;
}
// What the rental owner owes delivery people.
export async function getOwedDeliveryPayables(status) {
  const res = await client.get(`/api/${VERTICAL}/bookings/delivery-payables`, { params: status ? { status } : {} });
  return res.data.data;
}
export async function getOwnerFinanceSurface() {
  const res = await client.get(`/api/${VERTICAL}/items/owner-finance-surface`);
  return res.data.data;
}
export async function payDeliveryPayable(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/delivery-payables/${id}/pay`, payload);
  return res.data.data;
}
// Website owner / finance review.
export async function adminListDeliveryPayables(params = {}) {
  const res = await client.get('/api/admin/delivery-payables', { params });
  return res.data.data;
}
export async function adminRemindOwnerPayable(id) {
  const res = await client.post(`/api/admin/delivery-payables/${id}/remind-owner`);
  return res.data.data;
}
export async function adminResolveDeliveryPayable(id, payload) {
  const res = await client.patch(`/api/admin/delivery-payables/${id}`, payload);
  return res.data.data;
}
// Per-delivery-person fee override.
export async function adminSetDeliveryPayConfig(userId, payload) {
  const res = await client.patch(`/api/admin/${VERTICAL}/users/${userId}/delivery-pay`, payload);
  return res.data.data;
}

// ---- Shared-delivery inter-business billing (shared_delivery_inter_business_billing.md) ----
export async function recordFailedAttempt(id, { reason, waitingMinutes }) {
  const res = await client.patch(`/api/${VERTICAL}/delivery/${id}/failed-attempt`, { reason, waitingMinutes });
  return res.data.data;
}
export async function getInterBusinessCharges({ role, status } = {}) {
  const res = await client.get(`/api/${VERTICAL}/bookings/inter-business-charges`, { params: { role, status } });
  return res.data.data;
}
export async function payInterBusinessCharge(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/inter-business-charges/${id}/pay`, payload);
  return res.data.data;
}
export async function remindInterBusinessCharge(id) {
  const res = await client.post(`/api/${VERTICAL}/bookings/inter-business-charges/${id}/remind`);
  return res.data.data;
}
export async function escalateInterBusinessCharge(id, note) {
  const res = await client.post(`/api/${VERTICAL}/bookings/inter-business-charges/${id}/escalate`, { note });
  return res.data.data;
}
export async function adminListInterBusinessCharges(params = {}) {
  const res = await client.get('/api/admin/inter-business-charges', { params });
  return res.data.data;
}
export async function adminRemindInterBusinessOwner(id) {
  const res = await client.post(`/api/admin/inter-business-charges/${id}/remind-owner`);
  return res.data.data;
}
export async function adminResolveInterBusinessCharge(id, payload) {
  const res = await client.patch(`/api/admin/inter-business-charges/${id}`, payload);
  return res.data.data;
}
export async function adminSetSharedDeliveryPricing(userId, payload) {
  const res = await client.patch(`/api/admin/${VERTICAL}/users/${userId}/shared-delivery-pricing`, payload);
  return res.data.data;
}

// ---- Finance / settlements (Owner Bookings & Cancellation v2 doc) ----
// Not vertical-scoped like the admin functions above - settlement config, owner overrides, and
// settlements themselves span all 4 verticals (see routes/ownerCrud.js's financeRouter).

export async function getSettlementConfig() {
  const res = await client.get('/api/admin/settlement-config');
  return res.data.data;
}

export async function updateSettlementConfig(payload) {
  const res = await client.put('/api/admin/settlement-config', payload);
  return res.data.data;
}

// Owners for the finance-overrides picker. `withOverrides` -> only owners that already have a
// negotiated term; `q` -> free-text name/email search for the lookup typeahead.
export async function adminListOwners({ q, withOverrides } = {}) {
  const params = {};
  if (q) params.q = q;
  if (withOverrides) params.withOverrides = 1;
  const res = await client.get('/api/admin/owners', { params });
  return res.data.data;
}

// Returns the full envelope { data: <overrides>, owner: { _id, name, email } } - a lookup by
// name resolves to an account the caller didn't type, so the UI needs to know which one.
export async function getOwnerFinanceOverrides(ownerId) {
  const res = await client.get(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`);
  return res.data;
}

export async function updateOwnerFinanceOverrides(ownerId, payload) {
  const res = await client.put(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`, payload);
  return res.data;
}

export async function getSettlements(filters = {}) {
  const res = await client.get('/api/admin/settlements', { params: filters });
  return res.data.data;
}

export async function decideSettlement(id, action, payload = {}) {
  const res = await client.patch(`/api/admin/settlements/${id}/${action}`, payload);
  return res.data.data;
}

// Owner Bookings & Cancellation v2 doc, Story 15 - Finance's platform-wide (not vertical-scoped)
// queue of bookings whose deposit refund is still outstanding, and the action to resolve one.
// finance_employee can call getRefundPendingBookings (view-only) but financeMarkRefundSent 403s
// for them - only finance_lead/finance_manager/admin can actually mark one sent.
export async function getRefundPendingBookings() {
  const res = await client.get('/api/admin/refund-pending');
  return res.data.data;
}

export async function financeMarkRefundSent(id, { method, notes } = {}) {
  const res = await client.patch(`/api/admin/refund-pending/${id}`, { method, notes });
  return res.data.data;
}

// payements_requirements.md - Finance's platform-wide queue of owner-recorded payments still
// awaiting confirmation (payment.needsReconciliation), and the action to confirm one. View for all
// finance roles + admin; confirm is finance_lead/finance_manager/admin only.
export async function getPaymentsToReconcile() {
  const res = await client.get('/api/admin/payments-to-reconcile');
  return res.data.data;
}

export async function financeConfirmReconciliation(id, note) {
  const res = await client.patch(`/api/admin/payments-to-reconcile/${id}/confirm`, { note });
  return res.data.data;
}

// ---- v3_fix_issues #7 & #8 - website-owner business analytics ----
// Platform-wide (not vertical-scoped), same as the settlement functions above. summary =
// KPIs + previous-period deltas + time series + by-vertical / top-customer / top-owner
// breakdowns; ledger = the filterable booking-level revenue list.
export async function getBusinessAnalytics(params = {}) {
  const res = await client.get('/api/admin/analytics/summary', { params });
  return res.data.data;
}

export async function getFinanceLedger(params = {}) {
  const res = await client.get('/api/admin/analytics/ledger', { params });
  return res.data; // { data, pagination, totals }
}

// ---- v3_fix_issues #10 - Request a Demo (public submit + website-owner queue + contact settings) ----
export async function submitDemoRequest(payload) {
  const res = await client.post('/api/demo-requests', payload);
  return res.data;
}

export async function adminListDemoRequests(status) {
  const res = await client.get('/api/admin/demo-requests', { params: status ? { status } : {} });
  return res.data.data;
}

export async function adminUpdateDemoRequest(id, payload) {
  const res = await client.patch(`/api/admin/demo-requests/${id}`, payload);
  return res.data.data;
}

// v9 customer_support_tickets - platform-wide (raised from the homepage, no vertical).
export async function adminListSupportTickets(params = {}) {
  const res = await client.get(`/api/admin/support-tickets`, { params });
  return res.data.data;
}
export async function adminUpdateSupportTicket(id, payload) {
  const res = await client.patch(`/api/admin/support-tickets/${id}`, payload);
  return res.data.data;
}

export async function getPlatformSettings() {
  const res = await client.get('/api/admin/platform-settings');
  return res.data.data;
}

export async function updatePlatformSettings(payload) {
  const res = await client.put('/api/admin/platform-settings', payload);
  return res.data.data;
}

// homepage_refactor.md - "About this business" content (registration, experience, management
// team with photos) for the homepage's Businesses popup. Not vertical-scoped - editable for any
// of the 5 verticals from this same admin login, same reach as platform settings above.
export async function listBusinessProfiles() {
  const res = await client.get('/api/business-profiles');
  return res.data.data;
}

export async function updateBusinessProfile(vertical, payload) {
  const res = await client.put(`/api/admin/business-profiles/${vertical}`, payload);
  return res.data.data;
}

// "Our Team" page content (Docs/requirements/common/homepage/our_team_page.md) - website-owner
// configurable per scope ('platform' = the homepage team; a vertical key = that vertical's own).
export async function getTeamProfile(scope) {
  const res = await client.get(`/api/admin/team-profiles/${scope}`);
  return res.data.data;
}
export async function saveTeamProfile(scope, payload) {
  const res = await client.put(`/api/admin/team-profiles/${scope}`, payload);
  return res.data.data;
}
export async function resetTeamProfile(scope) {
  const res = await client.post(`/api/admin/team-profiles/${scope}/reset`);
  return res.data.data;
}

// v3_fix_issues #13 - a delivery person records the cash they collected on a Cash-on-Delivery
// booking (reference = the receipt / acknowledgement number).
export async function collectCash(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/delivery/${id}/collect-cash`, payload);
  return res.data.data;
}

// ================================================================================================
// v4_fix_issues - partial payments / pro-rata / exception billing / finance reconciliation.
// ================================================================================================

// #5 - owner/admin changes a booking's dates after the fact; the rental is recomputed pro-rata.
// Response: { msg, data: { booking, reconciliation, applied } }. applied=false means it's waiting
// for finance.
export async function adjustBookingBilling(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/bookings/${id}/adjust-billing`, payload);
  return res.data;
}

// #5 - the owner's own view of billing-adjustment reconciliation records (their listings).
export async function getMyBillingAdjustments(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/bookings/billing-adjustments`, { params });
  return res.data.data;
}

// #5 - finance queue of billing-adjustment reconciliations (platform-wide) + the decide action.
export async function getReconciliations(params = {}) {
  const res = await client.get('/api/admin/reconciliations', { params });
  return res.data.data;
}

export async function decideReconciliation(id, payload) {
  const res = await client.patch(`/api/admin/reconciliations/${id}`, payload);
  return res.data.data;
}

// #2/#4 - real_estate per-confirmed-visit platform fees the property owner owes.
export async function getPlatformCharges(params = {}) {
  const res = await client.get('/api/admin/platform-charges', { params });
  return res.data.data;
}

export async function decidePlatformCharge(id, payload) {
  const res = await client.patch(`/api/admin/platform-charges/${id}`, payload);
  return res.data.data;
}

// ================================================================================================
// Owner onboarding / lead pipeline (Docs/requirements/common/onboard_business_model/
// leads_business_flow_requirement.md) — the website owner's own outbound sales tracker: calls /
// WhatsApp / visits / discussions with a prospective business owner, one paginated page, open/
// close per lead. Admin-only.
// ================================================================================================

export async function listLeads(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/leads`, { params });
  return res.data; // { data, pagination, summary }
}
export async function getLead(id) {
  const res = await client.get(`/api/${VERTICAL}/leads/${id}`);
  return res.data.data;
}
export async function createLead(payload) {
  const res = await client.post(`/api/${VERTICAL}/leads`, payload);
  return res.data.data;
}
export async function updateLead(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/leads/${id}`, payload);
  return res.data.data;
}
export async function setLeadStatus(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/leads/${id}/status`, payload);
  return res.data.data;
}
export async function addLeadInteraction(id, payload) {
  const res = await client.post(`/api/${VERTICAL}/leads/${id}/interactions`, payload);
  return res.data.data;
}
export async function deleteLead(id) {
  const res = await client.delete(`/api/${VERTICAL}/leads/${id}`);
  return res.data.data;
}

// ================================================================================================
// Email log & suppression (email_log_and_suppression requirement) - every email the platform
// sends is logged; a rental owner can stop non-critical emails to one specific customer.
// ================================================================================================

export async function listEmailLog(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/emails`, { params });
  return res.data; // { data, pagination }
}
export async function adminListEmailLog(params = {}) {
  const res = await client.get('/api/admin/emails', { params });
  return res.data;
}
export async function listEmailSuppressions(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/emails/suppressions`, { params });
  return res.data;
}
export async function addEmailSuppression(payload) {
  const res = await client.post(`/api/${VERTICAL}/emails/suppressions`, payload);
  return res.data.data;
}
export async function removeEmailSuppression(id) {
  const res = await client.delete(`/api/${VERTICAL}/emails/suppressions/${id}`);
  return res.data.data;
}
export async function adminListEmailSuppressions(params = {}) {
  const res = await client.get('/api/admin/emails/suppressions', { params });
  return res.data;
}
export async function adminRemoveEmailSuppression(id) {
  const res = await client.delete(`/api/admin/emails/suppressions/${id}`);
  return res.data.data;
}
export async function updateEmailSuppression(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/emails/suppressions/${id}`, payload);
  return res.data.data;
}
export async function adminUpdateEmailSuppression(id, payload) {
  const res = await client.patch(`/api/admin/emails/suppressions/${id}`, payload);
  return res.data.data;
}
export async function listEmailCategoryBlocks(params = {}) {
  const res = await client.get('/api/admin/emails/category-blocks', { params });
  return res.data;
}
export async function addEmailCategoryBlock(payload) {
  const res = await client.post('/api/admin/emails/category-blocks', payload);
  return res.data.data;
}
export async function updateEmailCategoryBlock(id, payload) {
  const res = await client.patch(`/api/admin/emails/category-blocks/${id}`, payload);
  return res.data.data;
}
export async function removeEmailCategoryBlock(id) {
  const res = await client.delete(`/api/admin/emails/category-blocks/${id}`);
  return res.data.data;
}

// ---- Booking-completion invoices (auto-generated) ----
export async function getInvoices(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/invoices`, { params });
  return res.data;
}
export async function getInvoice(id) {
  const res = await client.get(`/api/${VERTICAL}/invoices/${id}`);
  return res.data.data;
}
export function invoicePdfUrl(id) {
  return `${API_BASE_URL}/api/${VERTICAL}/invoices/${id}/pdf`;
}
export async function updateInvoiceStatus(id, status) {
  const res = await client.patch(`/api/${VERTICAL}/invoices/${id}/status`, { status });
  return res.data.data;
}
export async function recordInvoicePayment(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/invoices/${id}/payment`, payload);
  return res.data.data;
}
export async function getInvoiceTemplate() {
  const res = await client.get(`/api/${VERTICAL}/invoice-template`);
  return res.data.data;
}
export async function saveInvoiceTemplate(payload) {
  const res = await client.put(`/api/${VERTICAL}/invoice-template`, payload);
  return res.data.data;
}
export async function fetchInvoicePdfBlob(id) {
  const res = await client.get(`/api/${VERTICAL}/invoices/${id}/pdf`, { responseType: 'blob' });
  return new Blob([res.data], { type: 'application/pdf' });
}
export function saveInvoicePdfBlob(blob, invoiceNumber) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${(invoiceNumber || 'draft').replace(/[^a-z0-9-]+/gi, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
}
export async function downloadInvoicePdf(id, invoiceNumber) {
  saveInvoicePdfBlob(await fetchInvoicePdfBlob(id), invoiceNumber);
}
